import React, {FC} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {EmojiDefinitionAndSkinTone} from '../../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {getEmojiHighlight} from '../../../../../helpers/models/highlightHelpers';
import {TagModel} from '../../../../../models/api/tagModel';
import {Emoji} from '../../../../common/emoji/emoji';
import {TooltipExplicitCondition} from '../../../../common/tooltips/tooltipExplicitCondition';

/*
 * Constants.
 */

const charactersLimit = 20;

/*
 * Props.
 */

interface ActivityTagPillContentProps {
  tagsPath: ReadonlyArray<TagModel>;
}

/*
 * Style.
 */

const StyledContentDiv = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
`;

const StyledTagDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledParentTagDiv = styled.div`
  display: flex;
  align-items: center;
  opacity: 0.5;
`;

const StyledEmojiDiv = styled.div`
  flex-shrink: 0;
  margin-right: 3px;

  margin-left: 1px;
  margin-bottom: -3px;

  & > img {
    margin-top: -5px;

    max-height: 10px;
    max-width: 10px;
  }
`;

const StyledChevronDiv = styled.div`
  margin: 0 1px;
`;

const StyledNameDiv = styled.div`
  flex-shrink: 0;
  margin-top: -2px;
`;

/*
 * Component.
 */

export const ActivityTagPillContent: FC<ActivityTagPillContentProps> = ({tagsPath}) => (
  <StyledContentDiv>{renderTags(tagsPath)}</StyledContentDiv>
);

function renderTags(tagsPath: ReadonlyArray<TagModel>) {
  let usedCharactersCount = 0;
  let lastDisplayName: string | null = null;
  let neededEllipsis = false;

  const tagsRender = tagsPath.map((tag, index) => {
    // If the previous item was already empty, nothing to render.
    if (index > 0 && !lastDisplayName) {
      return null;
    }

    // Find the name for the tag to render.
    const results = findTagDisplayName(tag, usedCharactersCount);
    lastDisplayName = results.displayName;
    usedCharactersCount = results.usedCharacterCount;
    neededEllipsis = neededEllipsis || results.neededEllipsis;

    // If this is not the first tag, render as a parent.
    if (index > 0 || !results.displayName) {
      return renderParentTag(tag, results.displayName || '…');
    }

    // Otherwise, render it full.
    return renderTag(tag, results.displayName);
  });

  return (
    <>
      {tagsRender}
      <TooltipExplicitCondition isEnabled={neededEllipsis} />
    </>
  );
}

function renderTag(tag: TagModel, displayName: string) {
  const emojiAndSkinTone = getEmojiHighlight(tag);
  return (
    <StyledTagDiv key={tag.id}>
      {emojiAndSkinTone && renderEmoji(emojiAndSkinTone)}
      <StyledNameDiv>{displayName}</StyledNameDiv>
    </StyledTagDiv>
  );
}

function renderEmoji(emojiAndSkinTone: EmojiDefinitionAndSkinTone) {
  return (
    <StyledEmojiDiv>
      <Emoji {...emojiAndSkinTone} size={14} />
    </StyledEmojiDiv>
  );
}

function renderParentTag(tag: TagModel, displayName: string) {
  return (
    <StyledParentTagDiv key={tag.id}>
      <StyledNameDiv>{displayName}</StyledNameDiv>
      <StyledChevronDiv>
        <Icon name="chevronRightTiny" />
      </StyledChevronDiv>
    </StyledParentTagDiv>
  );
}

/*
 * Helpers.
 */

function findTagDisplayName(tag: TagModel, usedCharactersCount: number) {
  // Find the name to display for this tag.
  const allowedCharactersCount = charactersLimit - usedCharactersCount;
  const shortName = tag.name.slice(0, allowedCharactersCount);
  const newUsedCharactersCount = usedCharactersCount + shortName.length;

  // If we need ellipsis and our name is short, just display the ellipsis.
  const needsEllipsis = tag.name.length > shortName.length;
  if (needsEllipsis && shortName.length < 3) {
    return {
      displayName: null,
      usedCharacterCount: newUsedCharactersCount,
      neededEllipsis: needsEllipsis,
    };
  }

  const shortNameWithEllipsis = needsEllipsis ? `${shortName}…` : shortName;
  return {
    displayName: shortNameWithEllipsis,
    usedCharacterCount: newUsedCharactersCount,
    neededEllipsis: needsEllipsis,
  };
}
